import { BoxSlotDraftEntity } from "@/modules/boxSlot/entities/boxSlotEntity"
import { type BoxSlotSessionInputKey } from "@/modules/boxSlotSession/formik/boxSessionFormikTypes"
import { word } from "@modules/core/utils/i18n"
import { requiredInput } from "@modules/form/formUtils"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { defaultDateTimeFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import * as Yup from "yup"

export type BoxSlotInputKey =
  | "date"
  | "startDateTime"
  | "endDateTime"
  | "programmationId"
  | "participantLimit"
  | "contentEarlyAccess"
  | "limitStart"
  | "limitEnd"
  | "limitCancel"
  | "allowMultiBooking"
  | "location"
  | "assignedStaffMembers"
  | "dropinsAccepted"
  | "trialsAccepted"
  | BoxSlotSessionInputKey

export type BoxSlotFormikType = {
  programmationId: string
  startDateTime: Date | null
  endDateTime: Date | null
  participantLimit: number
  contentEarlyAccess: boolean
  limitStart?: number // minutes
  limitEnd?: number // minutes
  limitCancel?: number // minutes
  location?: string
  allowMultiBooking: boolean
  assignedStaffMembers?: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean
}

export function getBoxSlotValidationSchema() {
  return Yup.object({
    date: Yup.date().required(),
    programmationId: requiredInput(Yup.string()),
    startDateTime: Yup.date()
      .required(word("global.form.required"))
      .test(
        "startDateTime",
        word("box.slotTemplateGroup.form.registration.end_error"),
        (value, { parent: { endDateTime } }) => {
          return value !== null && value !== undefined && dayjs(value).isBefore(endDateTime)
        },
      ),
    endDateTime: Yup.date()
      .required(word("global.form.required"))
      .test(
        "endDateTime",
        word("box.slotTemplateGroup.form.registration.end_error"),
        (value, { parent: { startDateTime } }) => {
          return value !== null && value !== undefined && dayjs(value).isAfter(startDateTime)
        },
      ),
    participantLimit: Yup.number().min(1).required("global.form.required"),
    location: Yup.string()
      .trim()
      .nullable()
      .max(40, word("form.maximum.characters", { count: 40 })),
    limitStart: Yup.number()
      .min(1, word("global.form.minimumValue", { minValue: 1 }))
      .required(word("global.form.required")),
    limitEnd: Yup.number()
      .min(1, word("global.form.minimumValue", { minValue: 1 }))
      .test("limitEnd", word("box.slotTemplateGroup.form.registration.end_error"), (value, context) => {
        return context.parent.limitStart !== null && !!value && context.parent.limitStart > value
      }),
    limitCancel: Yup.number()
      .min(1, word("global.form.minimumValue", { minValue: 1 }))
      .required(word("global.form.required")),
    allowMultiBooking: Yup.boolean().nullable(),
    assignedStaffMembers: Yup.array(),
    dropinsAccepted: Yup.boolean().nullable(),
    trialsAccepted: Yup.boolean().nullable(),
  })
}

export const convertBoxSlotToFormik = (slot: BoxSlotDraftEntity): BoxSlotFormikType => {
  return {
    programmationId: slot.programmationId,
    startDateTime: slot.startDateTime ? new Date(slot.startDateTime) : null,
    endDateTime: slot.endDateTime ? new Date(slot.endDateTime) : null,
    participantLimit: slot.participantLimit,
    contentEarlyAccess: slot.contentEarlyAccess,
    location: slot.location ?? "",
    limitStart: slot.participation?.limitStart ?? undefined,
    limitEnd: slot.participation?.limitEnd ?? undefined,
    limitCancel: slot.participation?.limitCancel ?? undefined,
    allowMultiBooking: slot.allowMultiBooking,
    assignedStaffMembers: slot.assignedStaffMembers,
    dropinsAccepted: slot.dropinsAccepted,
    trialsAccepted: slot.trialsAccepted,
  }
}

export const convertFormikToBoxSlotDraft = (
  formik: BoxSlotFormikType,
  color: string,
  programmationId: string,
  programmationName: string,
): BoxSlotDraftEntity => {
  const newDate = dayjs(formik.startDateTime as Date)
  const startDateTime = dayjs(formik.startDateTime)
    .set("year", newDate.year())
    .set("month", newDate.month())
    .set("date", newDate.date())
  const endDateTime = dayjs(formik.endDateTime)
    .set("year", newDate.year())
    .set("month", newDate.month())
    .set("date", newDate.date())

  const boxSlotDraft = new BoxSlotDraftEntity({
    color,
    programmationId,
    programmationName,
    startDateTime: dayjs(startDateTime).format(defaultDateTimeFormat),
    endDateTime: dayjs(endDateTime).format(defaultDateTimeFormat),
    participantLimit: formik.participantLimit,
    contentEarlyAccess: formik.contentEarlyAccess,
    participation: {
      limitStart: formik.limitStart ?? null,
      limitEnd: formik.limitEnd ?? null,
      limitCancel: formik.limitCancel ?? null,
    },
    location: formik.location,
    allowMultiBooking: formik.allowMultiBooking,
    assignedStaffMembers: formik.assignedStaffMembers ?? [],
    dropinsAccepted: formik.dropinsAccepted,
    trialsAccepted: formik.trialsAccepted,
  })
  return boxSlotDraft
}
